import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Trash2, ChevronRight } from "lucide-react";
import { formatINR } from "../lib/utils.fmt";
import { cn } from "../lib/utils";
import { useCart } from "../lib/cart";
import VariantPickerSheet from "./VariantPickerSheet";
import ProductImage from "./ProductImage";

/**
 * Shared product card — the ONE implementation used everywhere a product is
 * listed (Home category strips, the Products catalog grid, and anywhere
 * else in the future). Layout is controlled by `className` (the caller
 * overrides sizing — e.g. a fixed width for a horizontal strip vs. `w-full`
 * to fill a grid cell); all cart/variant behavior lives here, once.
 *
 * Card state (which variants are in the cart, at what quantity) is always
 * read live from the cart context — never mirrored into local state — so
 * it can't drift out of sync with the Cart/Checkout pages.
 *
 * Multi-variant products can have MULTIPLE cart lines simultaneously (one
 * per selected variant, e.g. 500ml x1 AND 1L x2 at once) — but the card
 * stays compact by showing only a one-row summary of the most recently
 * touched variant. Full per-variant quantity management (adding more
 * variants, adjusting each one's qty) happens in VariantPickerSheet, which
 * lists every variant with its own live +/- stepper.
 */
export default function ProductCard({ product: p, primary, className = "" }) {
  const { items, add, setQty, remove } = useCart();
  const [sheetOpen, setSheetOpen] = useState(false);

  const variants = p.variants && p.variants.length > 1 ? p.variants : null;

  // Every cart line belonging to this product (0, 1, or many — one per
  // distinct variant selected).
  const cartLines = items.filter((x) => x.id === p.id);
  // The one shown on the card: whichever line was added/adjusted last.
  const latestLine = cartLines.length
    ? cartLines.reduce((a, b) => (b.updatedAt || 0) > (a.updatedAt || 0) ? b : a)
    : null;
  const latestVariant = latestLine && variants ? variants.find((v) => v.id === latestLine.variant_id) : null;

  // The price actually shown on the card: once a variant is in the cart,
  // that variant's OWN price (snapshotted on the cart line itself, so it's
  // guaranteed to match what Cart/Checkout show) — not the product's base
  // "from" price. Before anything's added, falls back to the product's
  // base price exactly as before.
  const cardPrice = latestLine ? latestLine.price : p.price;
  const cardMrp = latestLine ? latestLine.mrp : p.mrp;
  const cardEffectivePrice = latestLine ? (latestLine.effective_price ?? latestLine.price) : (p.effective_price ?? p.price);
  const hasPersonal = cardEffectivePrice != null && cardEffectivePrice < cardPrice;

  const defaultVariant = variants ? (variants.find((v) => v.is_in_stock !== false) || variants[0]) : null;

  const addDefault = () => {
    if (!variants) { add(p, 1); return; }
    if (defaultVariant?.is_in_stock === false) return;
    add(p, 1, defaultVariant);
  };

  return (
    <div
      className={cn("shrink-0 w-40 bg-white rounded-2xl border border-neutral-200 overflow-hidden card-hover flex flex-col", className)}
      data-testid={`product-card-${p.id}`}
    >
      <Link to={`/app/products/${p.id}`} className="block" data-testid={`product-card-link-${p.id}`}>
        <div className="aspect-square bg-neutral-100 overflow-hidden relative">
          <ProductImage src={p.image_url} name={p.name} primary={primary} className="w-full h-full" />
          {cardMrp > cardPrice && (
            <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-md text-[10px] font-extrabold text-white" style={{ background: primary }}>
              {Math.round(((cardMrp - cardPrice) / cardMrp) * 100)}% OFF
            </span>
          )}
        </div>
        <div className="px-3 pt-3">
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-bold line-clamp-1">
            {variants ? p.category : p.pack_size}
          </div>
          <div className="font-semibold text-sm mt-0.5 line-clamp-2 min-h-[2.5rem]">{p.name}</div>
          <div className="flex items-center gap-1.5 mt-1">
            {variants && !latestLine && <span className="text-[10px] text-neutral-400 font-bold">from</span>}
            <span className="font-heading font-bold" data-testid={`card-price-${p.id}`}>{formatINR(cardPrice)}</span>
            {cardMrp > cardPrice && <span className="text-[11px] text-neutral-400 line-through">{formatINR(cardMrp)}</span>}
          </div>
        </div>
      </Link>

      <div className="px-3 pb-3 pt-2 flex flex-col gap-1.5 mt-auto">
        {!variants ? (
          <SingleVariantAction product={p} line={cartLines[0]} primary={primary}
                                add={add} setQty={setQty} remove={remove} />
        ) : cartLines.length === 0 ? (
          <>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="h-8 px-2.5 rounded-xl border border-neutral-200 bg-brand-cream text-xs font-bold inline-flex items-center justify-between gap-1"
              data-testid={`variant-selector-${p.id}`}
            >
              <span className="truncate">{defaultVariant?.label || "Select"}</span>
              <ChevronRight className="w-3.5 h-3.5 shrink-0 text-neutral-500" />
            </button>
            <button
              type="button"
              onClick={addDefault}
              disabled={defaultVariant?.is_in_stock === false}
              className={cn(
                "w-full h-8 rounded-xl font-bold text-xs inline-flex items-center justify-center gap-1 text-white",
                defaultVariant?.is_in_stock === false && "opacity-40 cursor-not-allowed"
              )}
              style={{ background: primary }}
              data-testid={`add-${p.id}`}
            >
              {defaultVariant?.is_in_stock === false ? "Out of stock" : (<><Plus className="w-3.5 h-3.5" /> Add to Cart</>)}
            </button>
          </>
        ) : (
          // Compact summary — the most recently touched variant, with
          // direct +/- for IT right on the card (no sheet needed for the
          // common case of just bumping quantity). Tapping the variant
          // label itself (not the stepper) opens the sheet to add/manage
          // other variants — the card never lists them all itself.
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="text-[10px] font-bold text-neutral-500 inline-flex items-center gap-0.5 px-0.5 -mb-0.5"
              data-testid={`variant-selector-${p.id}`}
            >
              <span className="truncate">{latestVariant?.label || latestLine.pack_size}</span>
              {cartLines.length > 1 && <span className="text-neutral-400">+{cartLines.length - 1} more</span>}
              <ChevronRight className="w-3 h-3 shrink-0" />
            </button>
            <div className="flex items-center gap-1">
              <div className="flex-1 h-8 rounded-xl flex items-center justify-between px-1 text-white" style={{ background: primary }}>
                <button type="button" onClick={() => setQty(latestLine.lineId, latestLine.qty - 1)}
                        className="w-6 h-6 rounded-lg bg-white/20 inline-flex items-center justify-center"
                        data-testid={`qty-minus-${p.id}`} aria-label={`Decrease ${latestVariant?.label || ""}`}>
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="font-mono font-extrabold text-xs" data-testid={`qty-value-${p.id}`}>{latestLine.qty}</span>
                <button type="button" onClick={() => setQty(latestLine.lineId, latestLine.qty + 1)}
                        className="w-6 h-6 rounded-lg bg-white/20 inline-flex items-center justify-center"
                        data-testid={`qty-plus-${p.id}`} aria-label={`Increase ${latestVariant?.label || ""}`}>
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <button type="button" onClick={() => remove(latestLine.lineId)}
                      className="w-8 h-8 shrink-0 rounded-xl border border-neutral-200 text-brand-clay inline-flex items-center justify-center"
                      data-testid={`remove-${p.id}`} aria-label="Remove from cart">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {hasPersonal && (
          <div
            className="inline-flex self-start px-1.5 py-0.5 rounded-md bg-brand-leaf/10 text-brand-leaf text-[9px] font-extrabold uppercase tracking-wider"
            data-testid={`personal-badge-${p.id}`}
          >
            Personal Discount
          </div>
        )}
      </div>

      {variants && (
        <VariantPickerSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          product={p}
          variants={variants}
          primary={primary}
        />
      )}
    </div>
  );
}

function SingleVariantAction({ product: p, line, primary, add, setQty, remove }) {
  if (!line) {
    return (
      <button
        type="button"
        onClick={() => add(p, 1)}
        className="w-full h-8 rounded-xl font-bold text-xs inline-flex items-center justify-center gap-1 text-white"
        style={{ background: primary }}
        data-testid={`add-${p.id}`}
      >
        <Plus className="w-3.5 h-3.5" /> Add to Cart
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <div className="flex-1 h-8 rounded-xl flex items-center justify-between px-1 text-white" style={{ background: primary }}>
        <button type="button" onClick={() => setQty(line.lineId, line.qty - 1)}
                className="w-6 h-6 rounded-lg bg-white/20 inline-flex items-center justify-center"
                data-testid={`qty-minus-${p.id}`} aria-label="Decrease quantity">
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="font-mono font-extrabold text-xs" data-testid={`qty-value-${p.id}`}>{line.qty}</span>
        <button type="button" onClick={() => setQty(line.lineId, line.qty + 1)}
                className="w-6 h-6 rounded-lg bg-white/20 inline-flex items-center justify-center"
                data-testid={`qty-plus-${p.id}`} aria-label="Increase quantity">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      <button type="button" onClick={() => remove(line.lineId)}
              className="w-8 h-8 shrink-0 rounded-xl border border-neutral-200 text-brand-clay inline-flex items-center justify-center"
              data-testid={`remove-${p.id}`} aria-label="Remove from cart">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
